import { Component, OnInit, Input, OnDestroy, OnChanges, AfterViewInit } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { Song } from 'src/app/models/song';
import { Collection } from 'src/app/models/collection';
import { AudioProvider } from 'src/app/providers/audio/audio.provider';
import { Course } from 'src/app/models/course';
import { Resources } from 'src/app/models/resources';
import { Teacher } from 'src/app/models/teacher';
import { Article } from 'src/app/models/article';
import { Video } from 'src/app/models/video';
import { TeachingTool } from 'src/app/models/teaching-tool';
import { MessagePopoverComponent } from '../message-popover/message-popover.component';
import { Common } from 'src/app/lib/common';
import { Subject, map, takeUntil } from 'rxjs';
import { TeacherStore } from 'src/app/stores/teacher.store';
import { ResourceStore } from 'src/app/stores/resources.store';
import { TeacherProvider } from 'src/app/providers/teacher/teacher.provider';
const Plyr = require("plyr");

@Component({
  selector: 'app-song-detail',
  templateUrl: './song-detail.component.html',
  styleUrls: ['./song-detail.component.scss']
})
export class SongDetailComponent implements OnInit {

  resources: Resources = Resources.defaults();
  teacher: Teacher = Teacher.defaults();

  ngUnsubscribeTeacher = new Subject<void>();
  ngUnsubscribeResources = new Subject<void>();

  waiting: boolean = true

  _course: Course = Course.defaults()
  get course(): Course { return this._course }
  @Input() set course(course: Course) { this._course = course || Course.defaults() }

  get collection(): Collection {
    if (this.isCustomSong) {
      return Collection.defaults()  // LessonSong does not have findCollection()
    } else {
      return this.song.findCollection(this.resources.collections) || Collection.defaults()
    }
  }

  _song: Song = Song.defaults()
  get song(): Song { 
    return this._song
  }
  @Input() set song(song: Song) { 
    this._song = song ||  Song.defaults();
    this.initializePlayer();
    this.teacherProvider.isCached(this._song.url).then((value: boolean) => {
      this._isCached = value
    })
  }

  @Input() set isVisible(visible: boolean) {
    if (!visible) {
      this.player?.stop();
    }
  }

  get isCustomSong(): boolean { return !this.song.findCollection }

  _isCached: boolean = false
  get isPlayable(): boolean {
    return navigator.onLine || this._isCached
  }

  player: any;

  constructor(
    public audioProvider: AudioProvider,
    private teacherStore: TeacherStore,
    private resourceStore: ResourceStore,
    private teacherProvider: TeacherProvider,
    private popoverCtrl: PopoverController) {

      this.teacher = this.teacherProvider.teacher;
      this.teacherStore.state$
          .pipe(takeUntil(this.ngUnsubscribeTeacher))
          .subscribe(() => {
            this.teacher = this.teacherProvider.teacher;
          });

      this.resources = this.resourceStore.getResources();
      this.resourceStore.state$
        .pipe(takeUntil(this.ngUnsubscribeResources))
        .subscribe(() => {
          this.resources = this.resourceStore.getResources();
          this.waiting = false;
        });
  }
  
  ngOnInit() {
    this.initializePlayer();
  }

  ngOnDestroy() {
    this.player?.destroy();
    this.player = undefined;
    this.ngUnsubscribeTeacher.next();
    this.ngUnsubscribeTeacher.complete();
    this.ngUnsubscribeResources.next();
    this.ngUnsubscribeResources.complete();
  }

  private initializePlayer() {
    this.player?.destroy();
    this.player = undefined;

    const controls = [
      'progress', // The progress bar and scrubber for playback and buffering
      'current-time', // The current time of playback
      'duration', // The full duration of the media
    ];
    if (this._song != undefined && this._song.url != '') {
      this.player = new Plyr('#plyrID', { controls , tooltips: false, resetOnEnd: true});
      this.player.source = {
        type: 'audio',
        title: this._song.name,
        sources: [
          {
            src: this._song.url,
            type: 'audio/mp3'
          }
        ]
      }
    }
  }

  recordingClass(): string {
    if (!this.song.url) {
      return ''
    }
    if (this.isCustomSong) {
      return 'mt-play-selected'
    }
    if (this.audioProvider.isPlaying) {
      return 'mt-pause-selected'
    } else {
      return 'mt-play-selected'
    }
  }

  playRecording() {
    this.player?.togglePlay();
  }

  availableVideos(song: Song): Video[] {
    return Video.videosWithSongTags(this.resources.videos, song.tagsList()).sort((a: Video, b: Video) => {
      if (a.category === b.category) {
        return a.name < b.name ? -1 : 1
      }

      for (const category of ['Classroom Footage', 'Song Samples', 'Song Card Videos', 'Original Teaching Videos', 'Demo Classes', 'How-To Videos', 'Professional Development']) {
        if (a.category.toLowerCase() === category.toLowerCase()) { return -1 }
        if (b.category.toLowerCase() === category.toLowerCase()) { return 1 }
      }

      return a.name < b.name ? -1 : 1
    })
  }

  additionalResources(): TeachingTool[] {
    return TeachingTool.toolsWithTags(this.resources.teachingTools, this.song.tagsList());
  }

  additionResourceParenthesisData(): TeachingTool[] {
    if (this.additionalResources().length == 0) {
      return [];
    }
    
    const parenthesisTags: any = this.additionalResources()
      .filter((a: TeachingTool) => a.name.match(/\((.*?)\)/))
      .sort((a: TeachingTool,b: TeachingTool) => a.name > b.name ? 1 : -1);
    return parenthesisTags;
  }

  additionResourceTSBTags(): TeachingTool[] {
    if (this.additionalResources().length == 0) {
      return [];
    }

    let data = this.additionalResources();
    const tsbTags: any = data
      .filter((a: TeachingTool) => a.tagsList().includes('tsb') || a.tagsList().includes('TSB'))
      .sort((a: TeachingTool,b: TeachingTool) => a.name > b.name ? 1 : -1);
    return tsbTags;
  }

  additionResourceOtherData(): TeachingTool[] {
    if(this.additionalResources().length == 0) {
      return [];
    }
    
    const otherTags: any = this.additionalResources()
      .filter((a: TeachingTool) => 
      !a.name.match(/\((.*?)\)/) && !(a.tagsList().includes('tsb') || a.tagsList().includes('TSB')))
      .sort((a: TeachingTool,b: TeachingTool) => a.name > b.name ? 1 : -1);
    return otherTags;
  }

  articles(song: Song): Article[] {
    return Article.articlesWithTags(this.teacher.articles, song.tagsList())
  }

  openUrl(url: string) {

    // Some of the songs have garbage text after the url, usually separated by a newline
    const n = url.indexOf('\n')
    const correctedUrl = url.substring(0, n === -1 ? url.length : n)
    if (correctedUrl) {
      window.open(correctedUrl, '_blank')
    } else {
      MessagePopoverComponent.present(this.popoverCtrl, 'No URL', 'This item cannot be displayed because it does not have a url.', true, false, [])
    }
  }
}
